class AdvancedSearch extends HTMLElement {
  static FILTER_DEBOUNCE_MS = 80;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const configElement = this.querySelector('[data-advanced-search-config]');
    if (!configElement) return;

    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Advanced search configuration is invalid.', error);
      this.setReady();
      return;
    }

    this.sourceValues = new Set(this.config.sources.filter((item) => item.enabled).map((item) => item.value));
    this.sortValues = new Map(this.config.sortOptions.map((item) => [item.value, item]));
    this.isPreview = Boolean(window.Shopify?.designMode || new URLSearchParams(window.location.search).has('preview_theme_id'));
    this.filterDialog = this.querySelector('[data-filter-dialog]');
    this.lastDialogTrigger = null;
    this.abortController = null;
    this.filterRequestTimer = null;
    this.requestSequence = 0;
    this.activeRequestKey = null;
    this.renderedRequestKey = null;
    this.state = this.readState();

    this.renderSortOptions();
    this.renderFilterControls();
    this.bindEvents();
    this.syncInterface();
    this.showDevelopmentNotice();
    this.updatePublicUrl('replace');

    if (this.shouldRequestInitialResults()) {
      this.requestResults({ historyMode: 'replace', initial: true });
    } else {
      this.setReady();
    }
  }

  readList(params, name, allowedValues) {
    const seen = new Set();

    return (params.get(name) || '')
      .split(',')
      .map((value) => value.trim())
      .filter((value) => {
        if (!allowedValues.has(value) || seen.has(value)) return false;
        seen.add(value);
        return true;
      });
  }

  readState() {
    const params = new URLSearchParams(window.location.search);
    const requestedSort = params.get('sort');
    const legacyShopifySort = params.get('sort_by');
    const sortFromShopify = this.config.sortOptions.find((item) => item.shopifyValue === legacyShopifySort)?.value;
    const sort = this.sortValues.has(requestedSort) ? requestedSort : sortFromShopify || 'relevance';
    const parsedPage = Number.parseInt(params.get('page') || '1', 10);

    return {
      query: params.get('q') || '',
      sources: this.readList(params, 'sources', this.sourceValues),
      sort,
      page: Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1,
    };
  }

  isExactIdentifier(query) {
    return this.getIdentifierParts(query) !== null;
  }

  getIdentifierParts(query) {
    const normalized = query.trim();
    const setMatch = normalized.match(/^(\d{4,7})(?:-(\d{1,3}))?$/);

    if (setMatch) {
      return {
        kind: 'set',
        full: normalized,
        base: setMatch[1],
        hasSuffix: Boolean(setMatch[2]),
      };
    }

    if (/^MOC-\d+$/i.test(normalized)) {
      return {
        kind: 'moc',
        full: normalized.toUpperCase(),
        base: normalized.toUpperCase(),
        hasSuffix: false,
      };
    }

    return null;
  }

  quoteSearchPhrase(value) {
    return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  }

  buildInternalQuery() {
    const clauses = [];
    const visibleQuery = this.state.query.trim();
    const identifier = this.getIdentifierParts(visibleQuery);

    if (visibleQuery) {
      if (identifier?.kind === 'set' && identifier.hasSuffix) {
        clauses.push(
          `(${this.quoteSearchPhrase(identifier.full)} OR ${this.quoteSearchPhrase(identifier.base)})`,
        );
      } else {
        clauses.push(identifier ? this.quoteSearchPhrase(identifier.full) : visibleQuery);
      }
    }

    const sourceValues =
      visibleQuery && this.state.sources.length === 0
        ? this.config.sources.filter((item) => item.enabled).map((item) => item.value)
        : this.state.sources;
    const sourceTags = sourceValues
      .map((value) => this.config.sources.find((item) => item.value === value)?.internalTag)
      .filter(Boolean);

    if (sourceTags.length) {
      clauses.push(`(${sourceTags.map((tag) => `tag:${tag}`).join(' OR ')})`);
    }

    return clauses.join(' AND ');
  }

  buildPublicUrl({ includePage = false } = {}) {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    [
      'q',
      'sources',
      'topics',
      'sort',
      'sort_by',
      'page',
      'type',
      'options',
      'options[prefix]',
      'section_id',
      'sections',
    ].forEach((name) => params.delete(name));

    if (this.state.query) params.set('q', this.state.query);
    if (this.state.sources.length) params.set('sources', this.state.sources.join(','));
    if (this.state.sort !== 'relevance') params.set('sort', this.state.sort);
    if (includePage && this.state.page > 1) params.set('page', String(this.state.page));

    const serializedParams = params.toString().replace(/%2C/gi, ',');
    return `${url.pathname}${serializedParams ? `?${serializedParams}` : ''}`;
  }

  buildInternalUrl() {
    const searchUrl = new URL(this.config.searchUrl, window.location.origin);
    const internalQuery = this.buildInternalQuery();
    const sortOption = this.sortValues.get(this.state.sort) || this.sortValues.get('relevance');

    searchUrl.searchParams.set('q', internalQuery);
    searchUrl.searchParams.set('type', this.config.resourceTypes.join(','));
    searchUrl.searchParams.set('options[prefix]', this.isExactIdentifier(this.state.query) ? 'none' : 'last');
    searchUrl.searchParams.set('section_id', this.dataset.sectionId);

    if (sortOption.shopifyValue !== 'relevance') {
      searchUrl.searchParams.set('sort_by', sortOption.shopifyValue);
    }

    if (this.state.page > 1) {
      searchUrl.searchParams.set('page', String(this.state.page));
    }

    return searchUrl;
  }

  shouldRequestInitialResults() {
    return Boolean(
      this.dataset.searchPerformed === 'true' ||
        this.state.query.trim() ||
        this.state.sources.length ||
        this.state.sort !== 'relevance' ||
        this.state.page > 1,
    );
  }

  renderSortOptions() {
    this.querySelectorAll('[data-advanced-sort]').forEach((select) => {
      select.replaceChildren();

      this.config.sortOptions.forEach((sortOption) => {
        const option = document.createElement('option');
        option.value = sortOption.value;
        option.textContent = sortOption.label;
        option.selected = sortOption.value === this.state.sort;
        select.append(option);
      });
    });
  }

  renderFilterControls() {
    this.querySelectorAll('[data-filter-controls]').forEach((container) => {
      const context = container.closest('[data-filter-content]')?.dataset.filterContext || 'filters';
      container.replaceChildren(this.createFilterGroup('sources', 'Content source', this.config.sources, context));
    });
  }

  createFilterGroup(kind, label, options, context) {
    const fieldset = document.createElement('fieldset');
    fieldset.className = 'advanced-search__filter-group';

    const legend = document.createElement('legend');
    legend.className = 'advanced-search__filter-legend';
    legend.textContent = label;
    fieldset.append(legend);

    if (this.isPreview && options.some((option) => option.status !== 'active')) {
      const pendingBadge = document.createElement('span');
      pendingBadge.className = 'advanced-search__pending-badge';
      pendingBadge.textContent = this.config.messages.pendingBadge;
      fieldset.append(pendingBadge);
    }

    options
      .filter((option) => option.enabled)
      .forEach((option) => {
        const row = document.createElement('label');
        row.className = 'advanced-search__check-row';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.value = option.value;
        input.dataset.filterKind = kind;
        input.dataset.filterValue = option.value;
        input.id = `AdvancedSearch-${context}-${kind}-${option.value}`;

        const text = document.createElement('span');
        text.textContent = option.label;
        row.append(input, text);
        fieldset.append(row);
      });

    if (this.isPreview && options.some((option) => option.status !== 'active')) {
      const help = document.createElement('p');
      help.className = 'advanced-search__filter-help';
      help.textContent = this.config.messages.filterHelp;
      fieldset.append(help);
    }

    return fieldset;
  }

  bindEvents() {
    this.querySelector('[data-search-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.state.query = event.currentTarget.elements.q.value;
      this.state.page = 1;
      this.requestResults({ historyMode: 'push' });
    });

    this.addEventListener('change', (event) => {
      const sortSelect = event.target.closest('[data-advanced-sort]');
      if (sortSelect) {
        this.changeSort(sortSelect.value);
        return;
      }

      const input = event.target.closest('[data-filter-kind]');
      if (!input) return;
      this.toggleFilter(input.dataset.filterKind, input.dataset.filterValue, input.checked);
    });

    this.querySelectorAll('[data-clear-filters]').forEach((button) => {
      button.addEventListener('click', () => this.clearFilters());
    });

    this.querySelectorAll('[data-open-filters]').forEach((button) => {
      button.addEventListener('click', () => this.openDialog(button));
    });

    this.querySelectorAll('[data-close-filters], [data-apply-filters]').forEach((button) => {
      button.addEventListener('click', () => this.closeDialog());
    });

    this.filterDialog?.addEventListener('click', (event) => {
      if (event.target === this.filterDialog) this.closeDialog();
    });

    this.filterDialog?.addEventListener('close', () => {
      this.lastDialogTrigger?.focus();
      this.lastDialogTrigger = null;
    });

    this.querySelector('[data-filter-chips]')?.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-remove-filter]');
      if (!chip) return;
      this.toggleFilter(chip.dataset.filterKind, chip.dataset.filterValue, false);
    });

    this.addEventListener('click', (event) => {
      const paginationLink = event.target.closest('.pagination a[href]');
      if (!paginationLink) return;
      event.preventDefault();
      const pageUrl = new URL(paginationLink.href, window.location.origin);
      this.state.page = Math.max(1, Number.parseInt(pageUrl.searchParams.get('page') || '1', 10));
      this.requestResults({ historyMode: 'push', scrollToResults: true });
    });

    window.addEventListener('popstate', () => {
      this.state = this.readState();
      this.syncInterface();
      this.requestResults({ historyMode: 'none' });
    });
  }

  changeSort(value) {
    this.state.sort = this.sortValues.has(value) ? value : 'relevance';
    this.state.page = 1;
    this.requestResults({ historyMode: 'push' });
  }

  toggleFilter(kind, value, checked) {
    if (kind !== 'sources') return;
    const values = this.state.sources;
    const nextValues = checked
      ? [...new Set([...values, value])]
      : values.filter((currentValue) => currentValue !== value);
    const stateChanged =
      nextValues.length !== values.length || nextValues.some((currentValue, index) => currentValue !== values[index]);

    if (!stateChanged) {
      this.syncInterface();
      return;
    }

    this.state.sources = nextValues;
    this.state.page = 1;
    this.syncInterface();
    this.scheduleFilterRequest();
  }

  clearFilters() {
    this.state.sources = [];
    this.state.sort = 'relevance';
    this.state.page = 1;
    this.syncInterface();
    this.requestResults({ historyMode: 'push' });
    this.announce('All filters cleared. The keyword was retained.');
  }

  updatePublicUrl(mode) {
    if (mode === 'none') return;
    const publicUrl = this.buildPublicUrl({ includePage: true });
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (publicUrl === currentUrl) return;
    window.history[mode === 'push' ? 'pushState' : 'replaceState']({}, '', publicUrl);
  }

  cancelFilterRequestTimer() {
    if (this.filterRequestTimer === null) return;
    window.clearTimeout(this.filterRequestTimer);
    this.filterRequestTimer = null;
  }

  invalidateActiveRequest() {
    this.requestSequence += 1;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
      this.activeRequestKey = null;
    }
  }

  scheduleFilterRequest() {
    this.cancelFilterRequestTimer();
    this.invalidateActiveRequest();
    this.setLoading(true);
    this.hideError();

    this.filterRequestTimer = window.setTimeout(() => {
      this.filterRequestTimer = null;
      this.requestResults({ historyMode: 'push' });
    }, AdvancedSearch.FILTER_DEBOUNCE_MS);
  }

  async requestResults({ historyMode = 'none', initial = false, scrollToResults = false } = {}) {
    this.cancelFilterRequestTimer();
    this.updatePublicUrl(historyMode);
    this.syncInterface();
    const requestUrl = this.buildInternalUrl();
    const requestKey = requestUrl.toString();

    if (requestKey === this.activeRequestKey) return;
    if (requestKey === this.renderedRequestKey) {
      this.invalidateActiveRequest();
      this.setLoading(false);
      return;
    }

    this.invalidateActiveRequest();
    const requestId = this.requestSequence;
    const controller = new AbortController();
    this.abortController = controller;
    this.activeRequestKey = requestKey;
    this.setLoading(true);
    this.hideError();

    try {
      const response = await fetch(requestUrl, {
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal: controller.signal,
      });

      if (response.status === 404 || response.status === 422) {
        throw new Error(`Search request failed with status ${response.status}.`);
      }
      if (!response.ok) {
        throw new Error(`Search request failed with status ${response.status}.`);
      }

      const responseText = await response.text();
      if (!responseText.trim() || responseText.trim() === 'null') {
        throw new Error('Search section was not returned.');
      }
      if (requestId !== this.requestSequence || controller.signal.aborted) return;

      const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const responseRoot = responseDocument.querySelector('advanced-search');
      const nextResults = responseRoot?.querySelector('.advanced-search__results');
      if (!responseRoot || !nextResults) {
        throw new Error('Search section markup is incomplete.');
      }
      if (requestId !== this.requestSequence || controller.signal.aborted) return;

      this.prepareResultsForDisplay(nextResults);
      const currentResults = this.querySelector('.advanced-search__results');
      currentResults.replaceWith(nextResults);
      this.dataset.serverSort = responseRoot.dataset.serverSort || 'relevance';
      this.renderSortOptions();
      this.syncInterface();

      const nextApplyText = responseRoot.querySelector('[data-apply-filters]')?.textContent;
      const currentApplyButton = this.querySelector('[data-apply-filters]');
      if (nextApplyText && currentApplyButton) currentApplyButton.textContent = nextApplyText.trim();

      this.decoratePaginationLinks();
      this.renderedRequestKey = requestKey;
      this.setReady();
      this.announce('Search results updated.');

      if (scrollToResults) {
        this.querySelector('.advanced-search__results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Advanced search update failed.', error);
      this.showError('Results could not be updated. Please try again.');
      this.setReady();
      if (initial) this.announce('The enhanced search request failed. Showing the server-rendered fallback results.');
    } finally {
      if (requestId === this.requestSequence && this.abortController === controller) {
        this.abortController = null;
        this.activeRequestKey = null;
        this.setLoading(false);
      }
    }
  }

  prepareResultsForDisplay(results) {
    this.removeDuplicateResults(results);

    const queryText = results.querySelector('.advanced-search__results-query');
    if (queryText) {
      queryText.textContent = this.state.query
        ? `Showing products and articles for \u201c${this.state.query}\u201d`
        : 'Showing matching products and articles';
    }

    this.reorderResultsForRelevance(results);
  }

  removeDuplicateResults(results) {
    const seenKeys = new Set();

    results.querySelectorAll('[data-resource-key]').forEach((card) => {
      const resourceKey = card.dataset.resourceKey;
      if (!resourceKey || !seenKeys.has(resourceKey)) {
        if (resourceKey) seenKeys.add(resourceKey);
        return;
      }

      card.closest('.advanced-search__grid-item')?.remove();
    });
  }

  buildDiverseOrder(items) {
    if (items.length < 2) return [...items];
    const first = items[0];
    const remaining = items.slice(1);
    const products = remaining.filter((item) => item.type === 'product');
    const articles = remaining.filter((item) => item.type === 'article');

    if (!products.length && first.type !== 'product') return [...items];
    if (!articles.length && first.type !== 'article') return [...items];

    const ordered = [first];
    let productIndex = 0;
    let articleIndex = 0;
    let nextType = first.type === 'product' ? 'article' : 'product';

    while (productIndex < products.length || articleIndex < articles.length) {
      if (nextType === 'product' && productIndex < products.length) {
        ordered.push(products[productIndex]);
        productIndex += 1;
        nextType = 'article';
      } else if (nextType === 'article' && articleIndex < articles.length) {
        ordered.push(articles[articleIndex]);
        articleIndex += 1;
        nextType = 'product';
      } else if (productIndex < products.length) {
        ordered.push(products[productIndex]);
        productIndex += 1;
      } else {
        ordered.push(articles[articleIndex]);
        articleIndex += 1;
      }
    }

    return ordered;
  }

  normalizeResultValue(value) {
    return String(value || '')
      .normalize('NFKC')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  containsIdentifier(value, identifier) {
    const normalizedValue = this.normalizeResultValue(value);
    const normalizedIdentifier = this.normalizeResultValue(identifier);
    const escapedIdentifier = normalizedIdentifier.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const boundary = /^\d/.test(normalizedIdentifier) ? '[^0-9]' : '[^a-z0-9]';
    return new RegExp(`(^|${boundary})${escapedIdentifier}($|${boundary})`, 'i').test(normalizedValue);
  }

  getIdentifierTier(item, identifier) {
    const searchableValue = `${item.title || ''} ${item.url || ''}`;

    if (this.containsIdentifier(searchableValue, identifier.full)) return 1;
    if (identifier.hasSuffix && this.containsIdentifier(searchableValue, identifier.base)) return 2;
    return identifier.hasSuffix ? 3 : 2;
  }

  buildIdentifierRankedOrder(items, identifier) {
    const tiers = new Map();

    items.forEach((item) => {
      const tier = this.getIdentifierTier(item, identifier);
      if (!tiers.has(tier)) tiers.set(tier, []);
      tiers.get(tier).push(item);
    });

    return [...tiers.keys()]
      .sort((left, right) => left - right)
      .flatMap((tier) => this.buildDiverseOrder(tiers.get(tier)));
  }

  reorderResultsForRelevance(results) {
    if (this.state.sort !== 'relevance') return;
    const grid = results.querySelector('.advanced-search__grid');
    if (!grid) return;

    const items = [...grid.children].map((node) => ({
      node,
      type: node.querySelector('[data-resource-key]')?.dataset.resourceKey?.split(':', 1)[0],
      title: node.querySelector('[data-resource-key]')?.dataset.resourceTitle || '',
      url: node.querySelector('[data-resource-key]')?.dataset.resourceUrl || '',
    }));
    const identifier = this.getIdentifierParts(this.state.query);
    const ordered = identifier
      ? this.buildIdentifierRankedOrder(items, identifier)
      : this.buildDiverseOrder(items);

    ordered.forEach((item) => grid.append(item.node));
  }

  setLoading(isLoading) {
    const results = this.querySelector('.advanced-search__results');
    const loading = this.querySelector('[data-loading-state]');
    if (results) results.setAttribute('aria-busy', String(isLoading));
    if (loading) loading.hidden = !isLoading;
  }

  setReady() {
    this.classList.add('advanced-search--ready');
  }

  showError(message) {
    const error = this.querySelector('[data-search-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  hideError() {
    const error = this.querySelector('[data-search-error]');
    if (error) error.hidden = true;
  }

  syncInterface() {
    const queryInput = this.querySelector('[data-search-form] input[name="q"]');
    if (queryInput) queryInput.value = this.state.query;

    this.querySelectorAll('[data-filter-kind]').forEach((input) => {
      input.checked = input.dataset.filterKind === 'sources' && this.state.sources.includes(input.dataset.filterValue);
    });

    this.querySelectorAll('[data-advanced-sort]').forEach((select) => {
      select.value = this.state.sort;
    });

    this.syncHiddenFields();
    this.renderChips();
  }

  syncHiddenFields() {
    const values = {
      sources: this.state.sources.join(','),
      sort: this.state.sort === 'relevance' ? '' : this.state.sort,
      sort_by: this.state.sort === 'relevance' ? '' : this.sortValues.get(this.state.sort)?.shopifyValue || '',
    };

    Object.entries(values).forEach(([name, value]) => {
      const input = this.querySelector(`[data-state-field="${name}"]`);
      if (!input) return;
      input.value = value;
      input.disabled = !value;
    });
  }

  renderChips() {
    const container = this.querySelector('[data-filter-chips]');
    const wrapper = this.querySelector('[data-active-filters]');
    if (!container || !wrapper) return;

    const active = this.state.sources.map((value) => ({
      kind: 'sources',
      value,
      config: this.config.sources,
    }));

    container.replaceChildren();
    active.forEach((filter) => {
      const label = filter.config.find((item) => item.value === filter.value)?.label || filter.value;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'advanced-search__chip';
      button.dataset.removeFilter = '';
      button.dataset.filterKind = filter.kind;
      button.dataset.filterValue = filter.value;
      button.setAttribute('aria-label', `Remove ${label} filter`);
      button.append(document.createTextNode(`${label} `));

      const symbol = document.createElement('span');
      symbol.setAttribute('aria-hidden', 'true');
      symbol.textContent = '\u00d7';
      button.append(symbol);
      container.append(button);
    });

    wrapper.hidden = active.length === 0;
    this.querySelectorAll('[data-active-filter-count]').forEach((count) => {
      count.hidden = active.length === 0;
      count.textContent = active.length ? `(${active.length})` : '';
    });
  }

  decoratePaginationLinks() {
    this.querySelectorAll('.pagination a[href]').forEach((link) => {
      const internalUrl = new URL(link.href, window.location.origin);
      const page = Math.max(1, Number.parseInt(internalUrl.searchParams.get('page') || '1', 10));
      const originalPage = this.state.page;
      this.state.page = page;
      link.href = this.buildPublicUrl({ includePage: true }) + internalUrl.hash;
      this.state.page = originalPage;
    });
  }

  showDevelopmentNotice() {
    const notice = this.querySelector('[data-development-notice]');
    if (!notice || !this.isPreview) return;
    notice.hidden = false;
    const text = notice.querySelector('[data-development-notice-text]');
    if (text) text.textContent = this.config.messages.pendingNotice;
  }

  openDialog(trigger) {
    if (!this.filterDialog) return;
    this.lastDialogTrigger = trigger;
    if (typeof this.filterDialog.showModal === 'function') this.filterDialog.showModal();
  }

  closeDialog() {
    if (this.filterDialog?.open) this.filterDialog.close();
  }

  announce(message) {
    const liveRegion = this.querySelector('[data-search-live-region]');
    if (liveRegion) liveRegion.textContent = message;
  }
}

if (!customElements.get('advanced-search')) {
  customElements.define('advanced-search', AdvancedSearch);
}
